import { useMutation, useQuery } from "@tanstack/react-query";
import {
  FINANCE_MODULE_ID,
  INDIAN_ISD_CODE,
  LEARN_VERTICAL_ID,
  SCA_VERTICAL_ID,
} from "constants/constants";
import { DAY, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import Cookies from "js-cookie";
import { toISTDateTime } from "utils/formatDate";

const uploadCsv = async ({ file, selectedScriptId, uploaderUserId }) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${uploaderUserId}&selected_script_id=${selectedScriptId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order."
    );
  }
};

export const useUploadCsv = () => {
  return useMutation({ mutationFn: uploadCsv });
};

const fetchHistory = async (user_id) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/get-all-files?uploader_user_id=${user_id}&module_id=${FINANCE_MODULE_ID}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch history. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders."
    );
  }
};

export const useFetchHistory = (user_id) => {
  return useQuery({
    queryKey: ["history", user_id],
    queryFn: () => fetchHistory(user_id),
  });
};

const fetchProcessedFile = async (fileId) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useFetchProcessedFile = (fileId) => {
  const query = useQuery({
    queryKey: ["processedFile", fileId],
    queryFn: () => fetchProcessedFile(fileId),
    enabled: !!fileId,
  });
  return {
    ...query,
    fetchProcessedFile,
  };
};

const transformKHUserToUserFormat = (khUser) => {
  return [
    {
      firstName: khUser.first_name || "",
      lastName: khUser.last_name || "",
      phone: khUser.phone,
      uamId: khUser.uam_id,
      verticalId: SCA_VERTICAL_ID,
    },
  ];
};

const fetchKHUser = async (phoneNumber) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;
  const authToken = Cookies.get("jweToken");

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.detail?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }

  const khUserData = await response.json();

  if (!khUserData?.users || khUserData.users.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }

  const transformedKHUsers = transformKHUserToUserFormat(khUserData.users[0]);
  return transformedKHUsers;
};

const transformLearnUsersWithValidation = (globalUsers) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  return globalUsers.accounts.flatMap((account) => {
    if (!Array.isArray(account.profiles)) return [];

    const matchingProfiles = account.profiles.filter(
      (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID)
    );

    if (matchingProfiles.length > 1) {
      throw new Error(
        "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution."
      );
    }

    return matchingProfiles.map((profile) => ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    }));
  });
};

const fetchLearnUser = async (phoneNumber) => {
  const authToken = Cookies.get("jweToken");
  const url = process.env.REACT_APP_ONE_APP_SEARCH_USER_URL;

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const globalUsers = await response.json();

  if (response.status !== 200) {
    throw new Error("An error occurred while requesting Customer Information.");
  }

  const filtered_user_data = transformLearnUsersWithValidation(globalUsers);

  if (filtered_user_data.length === 0) {
    throw new Error("No user found for the provided phone number");
  }

  return filtered_user_data;
};

const fetchCustomerInfo = async (payload) => {
  if (Number(payload.selectedBusinessVertical) === SCA_VERTICAL_ID) {
    return await fetchKHUser(payload.phoneNumber);
  }
  if (Number(payload.selectedBusinessVertical) === LEARN_VERTICAL_ID) {
    return await fetchLearnUser(payload.phoneNumber);
  }
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const fetchCustomerOrderDetails = async (customerId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: customerId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCustomerOrderDetails = (customerId) => {
  return useQuery({
    queryKey: ["customer_order_details", customerId],
    queryFn: () => fetchCustomerOrderDetails(customerId),
    enabled: !!customerId,
  });
};

const fetchOrderDetails = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-info/${orderId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchOrderDetails = (orderId) => {
  return useQuery({
    queryKey: ["order_details", orderId],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
  });
};

const updateChequeStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cheque-status`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useUpdateChequeStatus = () => {
  return useMutation({ mutationFn: updateChequeStatus });
};

const updateBankTransferStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-bank-transfer-status`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useUpdateBankTransferStatus = () => {
  return useMutation({ mutationFn: updateBankTransferStatus });
};

const banks = async () => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-banks-list`);
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};
export const useFetchAllBanks = () => {
  return useQuery({
    queryKey: ["Banks"],
    queryFn: () => banks(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const generateOtp = async (phoneNumberAndCaptcha) => {
  const url = new URL(
    `${process.env.REACT_APP_COMMSERVICE_URL}/api/v3/generate-otp`
  );
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    isd_code: INDIAN_ISD_CODE,
    phone: phoneNumberAndCaptcha.phone,
    tenant_id: 1,
    product_id: 300,
    captcha_token: phoneNumberAndCaptcha.captcha_token,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

const _morph_user_tenants = (data) => {
  return Object.values(data).map(({ id, name }) => ({
    value: id,
    label: name,
  }));
};

export const fetchUserTenants = async (tenantIds) => {
  if (!Array.isArray(tenantIds) || tenantIds.length === 0) {
    throw new Error("No gateway tenant IDs found");
  }
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_URL}/get-one-oms-tenants-from-gateway-tenants`
  );

  tenantIds.forEach((id) => {
    url.searchParams.append("gateway_tenant_id", id);
  });

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      accept: "application/json",
      "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_SECRET_KEY,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || "Failed to fetch centers");
  }

  return _morph_user_tenants(data.data);
};

export const useFetchUserTenants = () => {
  const tenantIds = JSON.parse(localStorage.getItem("gatewayTenantIds")) || [];

  return useQuery({
    queryKey: ["userTenants"],
    queryFn: () => fetchUserTenants(tenantIds),
  });
};
export const fetchDaySheets = async (filters) => {
  const { centerIds, startDate, endDate, daySheetType, applyFilter } = filters;

  if (!centerIds || !startDate || !endDate || !daySheetType || !applyFilter) {
    throw new Error("Missing required filters to fetch day sheets");
  }

  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-day-sheets`);

  centerIds.forEach((id) => {
    url.searchParams.append("tenant_ids", id);
  });
  url.searchParams.set("day_sheet_close_time_from", toISTDateTime(startDate));
  url.searchParams.set("day_sheet_close_time_to", toISTDateTime(endDate));
  url.searchParams.set("type", daySheetType);

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || `Failed to fetch day sheets.`);
  }

  if (data?.status === "success") {
    return data.data;
  }

  throw new Error(
    data?.message || "An error occurred while fetching day sheets"
  );
};

export const useFetchDaySheets = ({ filters = {} }) => {
  const isEnabled = filters?.applyFilter === true;
  return useQuery({
    queryKey: ["daySheets", filters],
    queryFn: () => fetchDaySheets(filters),
    enabled: !!isEnabled,
  });
};

const downloadDaySheet = async (daySheetId, filters) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/download-day-sheet`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  url.searchParams.set("id", daySheetId);
  url.searchParams.set("type", filters.daySheetType);
  url.searchParams.set("format", filters.format);

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useDownloadDaySheet = () => {
  const query = useQuery({
    queryKey: ["downloadDaySheet"],
    queryFn: () => downloadDaySheet(),
  });
  return {
    ...query,
    downloadDaySheet: (daySheetId, filters) => downloadDaySheet(daySheetId, filters),
  };
};
